<script setup lang="ts">
import { createReusableTemplate } from '@vueuse/core'
import type { FormAttributes, FormInputs } from '~/models/FileForm'
import type { PreSignedFile } from '~/models/Media'
import {
  faArrowDownToLine,
  faArrowUpToLine,
  faSpinner,
  faTrash
} from '@fortawesome/duotone-regular-svg-icons'
import { computed, nextTick, onMounted, ref, useTemplateRef, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import Tooltip from '~/components/ui/Tooltip.vue'
import useFileInfo from '~/composables/useFileInfo'
import useFileUpload from '~/composables/useFileUpload'
import { PendoFeature } from '~/models/Pendo'
import Button from "~/components/ui/Button.vue"
import { useAppColor } from "~/composables/useAppColor"
import { AuToast } from "~/stores/toasts"
import AuCallout from "~/components/ui/AuCallout.vue"

type ViewMode = 'upload' | 'edit' | 'download'

const props = defineProps<{
  fileTitle?: string
  fileKey?: string
  mimeType?: string
  canEdit?: boolean
}>()

const emits = defineEmits<{
  saveUpload: [file: PreSignedFile],
  removeUpload: [],
}>()

const [DefineActionButtons, ReuseActionButtons] = createReusableTemplate()
const editInput = useTemplateRef<HTMLInputElement>('editNameInput')

const { t } = useI18n()
const { readableMimeType, getFileIcon } = useFileInfo()
const { themeColor } = useAppColor()
const {
  isUploading,
  isDownloading,
  fileTooLarge,
  postUploadFile,
  downloadFileKey,
  MAX_FILE_SIZE_MB,
  MIN_FILE_NAME,
  MAX_FILE_NAME,
} = useFileUpload()

const formAttributes = ref<FormAttributes>({ action: '', method: '', enctype: '' })
const formInputs = ref<Partial<FormInputs>>({})

const preSignedFile = ref<PreSignedFile>()
const viewMode = ref<ViewMode>('upload')
const isDragging = ref(false)
const fileName = ref<string>('')
const originalFileName = ref<string>('')

const hasForm = computed(() => formAttributes.value && formInputs.value)
const canEditName = computed(() => props.canEdit && props.fileKey && !isDownloading.value)
const canRemove = computed(() => props.canEdit && props.fileKey && !isDownloading.value)
const canDownload = computed(() => props.fileKey && !isDownloading.value)
const validFileName = computed(() => props.canEdit && fileName.value.length > MIN_FILE_NAME && fileName.value !== originalFileName.value)
const safeTitle = computed(() => props.fileTitle?.replace(/<[^>]*>/g, '').trim().slice(0, 20))

const fileType = computed(() => {
  const readable = props.mimeType && readableMimeType(props.mimeType)
  return readable || t('mimeType.file')
})

const downloadIcon = computed(() => {
  if (isDownloading.value) {
    return faSpinner
  }
  return props.mimeType && getFileIcon(props.mimeType) || faArrowDownToLine
})

const uploadLabel = computed(() => isUploading.value
  ? t('media.loading')
  : t('media.uploadFile')
)

async function uploadFile(file: File) {
  if (!props.canEdit) {
    throw new Error('Not allowed to upload file')
  }
  return postUploadFile(file)
    .then((response) => {
      preSignedFile.value = response
      fileName.value = <string>preSignedFile.value.name
      viewMode.value = 'download'
      saveFile()
    }).then(data => data)
    .catch((error) => {
      AuToast.error(t('media.unknownError'))
      throw error
    })
}

function downloadFile(fileKey: string) {
  downloadFileKey(fileKey)
    .catch((error) => {
      AuToast.error(t('media.downloadError'))
      throw error
    })
}

function dropFile(event: DragEvent) {
  if (!props.canEdit) {
    AuToast.error(t('media.errorNotAllowed'))
    return
  }
  event.preventDefault()
  isDragging.value = false
  const file = event?.dataTransfer?.files[0]
  if (!file) {
    return
  }
  uploadFile(file as File)
}

function changeFile(event: Event) {
  if (!props.canEdit) {
    AuToast.error(t('media.errorNotAllowed'))
    return
  }
  const files = (event.target as HTMLInputElement).files
  if (!files || !files[0]) {
    return
  }
  const file = files[0]
  uploadFile(file)
  if (preSignedFile.value) {
    emits('saveUpload', preSignedFile.value)
  }
}

function dragOverFile(event: Event) {
  event.preventDefault()
  isDragging.value = true
}

function dragLeaveFile() {
  isDragging.value = false
}

function saveFile() {
  if (!props.canEdit || !preSignedFile.value) {
    return
  }
  preSignedFile.value.name = fileName.value.slice(0, MAX_FILE_NAME)
  emits('saveUpload', preSignedFile.value)
  viewMode.value = 'download'
}

function removeFile() {
  if (!props.canEdit) {
    AuToast.error(t('media.errorNotAllowed'))
    return
  }
  preSignedFile.value = undefined
  viewMode.value = 'upload'
  fileName.value = ''
  fileTooLarge.value = false
  emits('removeUpload')
}

onMounted(() => {
  if (props.fileKey) {
    fileName.value = props.fileTitle ?? props.fileKey.split('/').pop() ?? ''
    originalFileName.value = fileName.value
    viewMode.value = 'download'
    preSignedFile.value = {
      name: fileName.value,
      key: props.fileKey,
      mimeType: props.mimeType ?? '',
    }
  }
})

watch(props, () => {
  viewMode.value = props.fileKey ? 'download' : 'upload'
  originalFileName.value = fileName.value
})

watch(viewMode, () => {
  if (viewMode.value === 'edit') {
    nextTick(() => editInput.value?.focus())
  }
})
</script>

<template>
  <div
    class="bg-(--theme-5) p-4 rounded-xl"
    :class="isDownloading || fileKey ? 'border-(--theme-20)' : 'border-(--theme-5)'"
  >
    <DefineActionButtons>
      <div class="flex gap-2 items-center" v-if="canDownload || canRemove">
        <Tooltip :content="t('media.download')" v-if="canDownload">
          <Button
            shape="square"
            variant="outline"
            size="lg"
            :color="themeColor"
            :disabled="!canDownload"
            :aria-label="t('media.download')"
            :data-pendo="PendoFeature.FileUploader.DownloadButton"
            @click="() => fileKey ? downloadFile(fileKey) : null"
          >
            <font-awesome-icon
              :icon="isDownloading ? faSpinner : faArrowDownToLine"
              :spin="isDownloading"
              :class="{ 'opacity-70 grayscale': !canDownload }"
            />
          </Button>
        </Tooltip>
        <Tooltip :content="t('media.removeFile')" v-if="canRemove">
          <Button
            shape="square"
            :disabled="!canRemove"
            variant="outline"
            :color="themeColor"
            size="lg"
            :aria-label="t('media.removeFile')"
            :data-pendo="PendoFeature.FileUploader.RemoveButton"
            @click="removeFile"
          >
            <font-awesome-icon :icon="faTrash" :class="{ 'opacity-70 grayscale': !canRemove }" />
          </Button>
        </Tooltip>
      </div>
    </DefineActionButtons>
    <div v-if="viewMode === 'upload'" class="flex flex-row justify-between gap-4">
      <AuCallout
        v-if="fileTooLarge"
        variant="error"
        :title="t('media.fileIsTooLargeTitle')"
        class="mb-6"
      >
        {{ t('media.fileIsTooLargeText', { mb: MAX_FILE_SIZE_MB }) }}
      </AuCallout>
      <form
        v-if="hasForm"
        class="group grow"
        v-bind="formAttributes"
      >
        <input
          id="fileInput"
          class="absolute m-0 size-0 overflow-hidden opacity-0"
          type="file"
          name="file"
          :disabled="!canEdit"
          :data-pendo="PendoFeature.FileUploader.UploadInput"
          @change="changeFile"
        >
        <label
          for="fileInput"
          class="flex items-center gap-4 group py-3 px-5 rounded-xl hover:bg-(--theme-10) transition-colors group-focus-within:ring grow"
          :class="{ 'opacity-70 grayscale !cursor-not-allowed': isDragging || !canEdit || isUploading, 'cursor-pointer': !isUploading }"
          @dragover="dragOverFile"
          @dragleave="dragLeaveFile"
          @drop="dropFile"
        >
          <div class="flex items-center justify-center size-[4em] bg-(--theme-10) group-hover:bg-(--theme-5) transition-colors text-(--theme-50) rounded-full pointer-events-none">
            <font-awesome-icon
              :icon="faArrowUpToLine"
              size="2xl"
            />
          </div>
          <span
            class="font-bold pointer-events-none"
            v-html="uploadLabel"
          />
        </label>
      </form>
      <ReuseActionButtons />
    </div>
    <div
      v-if="viewMode === 'edit'"
      class="flex flex-col gap-6 p-4"
    >
      <input
        v-model="fileName"
        ref="editNameInput"
        type="text"
        class="w-full rounded-md border-2 border-(--theme-50) px-4 py-3 ring-(--theme-50) transition placeholder:text-gray-40 hover:ring-1 focus-visible:ring-2"
        :maxlength="MAX_FILE_NAME"
        name="fileName"
        :placeholder="t('media.filenamePlaceholder')"
      />
      <p
        v-if="!fileName.length"
        class="text-coral-40"
        v-text="t('media.missingFilename')"
      />
      <div class="flex gap-2">
        <Button
          v-if="fileKey"
          :disabled="!validFileName"
          :color="themeColor"
          :data-pendo="PendoFeature.FileUploader.UpdateName"
          size="lg"
          @click="saveFile"
        >
          {{ t('save') }}
        </Button>
        <Button
          v-if="fileKey"
          variant="outline"
          :color="themeColor"
          size="lg"
          @click="viewMode = 'download'"
        >
          {{ t('cancel') }}
        </Button>
      </div>
    </div>
    <div
      v-if="viewMode === 'download'"
      class="flex flex-col sm:flex-row justify-start sm:justify-between gap-4 items-center"
    >
      <div
        v-if="fileKey"
        class="flex items-center gap-4 py-3 px-5 rounded-xl transition-colors focus-visible:ring grow"
        :data-pendo="PendoFeature.FileUploader.DownloadButton"
      >
        <div class="flex items-center justify-center size-[4em] bg-(--theme-10) text-(--theme-50) rounded-full">
          <font-awesome-icon
            :icon="downloadIcon"
            size="2xl"
          />
        </div>
        <div v-if="safeTitle" class="flex flex-col items-start gap-1.5">
           <span
             class="font-bold text-[1em]"
             v-text="t('media.uploadReady', { fileType })"
           />
          <Tooltip v-if="canEdit" :content="t('media.updateFilename')">
            <span
              class="text-[.8em] font-digital cursor-pointer"
              @click.prevent="viewMode = 'edit'"
              v-text="safeTitle"
            />
          </Tooltip>
          <span
            v-else
            class="text-[.8em] font-digital"
            v-text="safeTitle"
          />
        </div>
        <span
           v-else
           class="font-bold"
           v-text="t('media.uploadReady', { fileType })"
         />
      </div>
      <ReuseActionButtons />
    </div>
  </div>
</template>

